import { debounceOnSavePaper } from '@chegg-wt/core/state/paraphraser/utils'
import {
  adjustSentenceOffsets,
  setFeedbacks,
  setPaperRequiresRecheck,
  setWordCount,
} from '@chegg-wt/core/state/paraphraser/actions'
import { SentenceOffset } from '@chegg-wt/core/state/paraphraser/types'
import type { Dispatch } from 'redux'
import {
  Feedback,
  FeedbackType,
  ProcessTransactionSteps,
  UpdatedEditor,
} from '@chegg-wt/core/types/paraphraser'

import type { ReplaceStep } from '@tiptap/pm/transform'
import { newFeedbacksOnEditHandler } from '@chegg-wt/core/utils/paraphraser/pusherDataHandlers/newFeedbacksOnEditHandler'

import {
  feedbackTypeRequiresNoRecheck,
  IS_HISTORY_CHANGE,
  IS_NON_MANUAL_TEXT_UPDATE,
  NON_MANUAL_UPDATE_TYPE,
} from '@chegg-wt/core/utils/paraphraser/constants'
import history from '@chegg-wt/core/utils/paraphraser/history/history'
import { OnEditorUpdateParams } from './types'
import { getWordCount } from './helpers'
import { debounceOnCommitHistory } from './history/utils'

export const handleManualEdits = (
  sentencesEdited: { id: string }[],
  updatedOffsets: SentenceOffset[],
  feedbacks: Feedback[],
  dispatch: Dispatch,
  editor: UpdatedEditor,
  nonManualUpdateType: FeedbackType
) => {
  const filteredFeedbacks = feedbacks.filter((feedback) => {
    const indexInSentenceEdited = sentencesEdited.findIndex(
      (sentenceEdited) => sentenceEdited.id === feedback.sentenceId
    )
    if (indexInSentenceEdited === -1) return true
    return false
  })

  // For grammar manual update type we will remove the underline direclty with remove,replace command
  if (nonManualUpdateType !== FeedbackType.GRAMMAR)
    editor.commands.forEach(sentencesEdited, (sentenceEdited, { chain }) => {
      const { id, offset, length } =
        updatedOffsets.find((sentence) => sentence.id === sentenceEdited.id) || {}
      if (!id) return true
      return chain()
        .removeCustomHighlight({ id, offset, length })
        .removeCustomUnderline({ id, offset, length })
        .run()
    })

  dispatch(setFeedbacks(filteredFeedbacks))
}

export const reCalculateSentenceOffsets = (
  newText: string,
  from: number, // including
  to: number, // non-including
  sentences: SentenceOffset[],
  lastUpdated: number
) => {
  const updatedSentences = []
  const sentencesEdited = []
  const changeInLength = newText.length - (to - from)
  let i = 0
  while (i < sentences.length && sentences[i].offset + sentences[i].length <= from) {
    updatedSentences.push(sentences[i])
    i += 1
  }

  if (i < sentences.length && from >= sentences[i].offset) {
    // Change starts between this sentence
    const { originalText: originalSentence, offset: originalSentenceOffset } = sentences[
      i
    ]
    // const originalSentenceEnd = originalSentenceOffset+originalSentenceLength

    const updatedSentence =
      originalSentence.substring(0, from - originalSentenceOffset) +
      newText +
      originalSentence.substring(to - originalSentenceOffset)
    if (updatedSentence.length > 0)
      updatedSentences.push({
        ...sentences[i],
        originalText: updatedSentence,
        length: updatedSentence.length,
        lastUpdated,
      })
    sentencesEdited.push({ id: sentences[i].id, originalText: updatedSentence })
    i += 1
  }

  // Change include all of these full sentences, we will remove them from our sentence
  while (i < sentences.length && to >= sentences[i].offset + sentences[i].length) {
    // not taking these sentences as they are fully deleted in the change
    sentencesEdited.push({ id: sentences[i].id })
    i += 1
  }

  // Change include first half of this sentence
  if (i < sentences.length && to > sentences[i].offset) {
    const updatedSentence = sentences[i].originalText.substring(to - sentences[i].offset)
    updatedSentences.push({
      ...sentences[i],
      originalText: updatedSentence,
      offset: from + newText.length,
      lastUpdated,
    })
    sentencesEdited.push({ id: sentences[i].id, originalText: updatedSentence })
    i += 1
  }

  while (i < sentences.length) {
    updatedSentences.push({
      ...sentences[i],
      offset: sentences[i].offset + changeInLength,
    })
    i += 1
  }

  return { updatedSentences, sentencesEdited }
}

export const updateWordCountIfChanged = (
  updatedText: string,
  previousWordCount: number,
  paperRequiresRecheck: boolean,
  dispatch: Dispatch
): void => {
  const newWordCount = getWordCount(updatedText)

  if (newWordCount === 0) {
    if (paperRequiresRecheck) {
      dispatch(setPaperRequiresRecheck(false))
      history.setEnableReviewButton(false)
    }
  }

  if (newWordCount !== previousWordCount) {
    dispatch(setWordCount(newWordCount))
  }
}

export const processTransactionSteps = ({
  steps,
  sentenceOffsets,
  feedbacks,
  nonManualUpdateType,
  updatedEditor,
  transaction,
  dispatch,
  paperId,
  isHistoryChange,
  draftQuickActionsFlagEnabled,
  isDraftPaper,
}: ProcessTransactionSteps): void => {
  steps.forEach((step: ReplaceStep) => {
    const stepData = step.toJSON()
    if (stepData.stepType === 'replace') {
      let { from, to } = stepData
      if (from > to) {
        ;[from, to] = [to, from]
      }
      const inverted = step.invert(transaction.before)
      // Used to add timestamps for updated sentences
      const sentenceOffsetsCopy = sentenceOffsets.map((sentence) => ({ ...sentence }))
      const initiatedAt = new Date()

      const newText = step.slice.content.textBetween(
        0,
        step.slice.content.size,
        '\r\n',
        ''
      )

      if (!isHistoryChange) {
        history.addStep(inverted)
      }

      // No need to do all sentence offset correction logic when we not have any offset, it's just small optimization
      if (sentenceOffsets.length === 0) {
        if (!isHistoryChange) debounceOnCommitHistory(Boolean(nonManualUpdateType))
        return
      }

      const { updatedSentences, sentencesEdited } = reCalculateSentenceOffsets(
        newText,
        from,
        to,
        sentenceOffsetsCopy,
        initiatedAt.getTime()
      )

      // We not want to run the following logic for history changes
      if (isHistoryChange) {
        dispatch(adjustSentenceOffsets(updatedSentences))
        return
      }

      // Add these removing sentences in history
      history.handleRemoveFeedbackSentences(sentencesEdited)

      let sentencesAfterManualBoundaryRemoval = updatedSentences
      // We will run below logic for manual updates and the rewrite/expand/shorten updates
      if (
        draftQuickActionsFlagEnabled &&
        (!nonManualUpdateType || nonManualUpdateType === FeedbackType.REWRITE)
      ) {
        const editedSentenceIds = new Set(sentencesEdited.map(({ id }) => id))
        sentencesAfterManualBoundaryRemoval = updatedSentences.filter(
          ({ id }) => !editedSentenceIds.has(id)
        )
        const removedSentences = sentenceOffsetsCopy
          .filter((sentence) => editedSentenceIds.has(sentence.id))
          .map((sentence) => ({
            id: sentence.id,
            offset: sentence.offset,
            length: sentence.length,
          }))
        history.addSentencesRemovedOnManualEdit(removedSentences)
      }

      dispatch(adjustSentenceOffsets(sentencesAfterManualBoundaryRemoval))

      // delete all existing related feedbacks
      // For grammar we will handle it directly on apply as we need to only remove the applied feedback
      if (nonManualUpdateType !== FeedbackType.GRAMMAR) {
        handleManualEdits(
          sentencesEdited,
          updatedSentences,
          feedbacks,
          dispatch,
          updatedEditor,
          nonManualUpdateType
        )
      }

      // Preserve cursor position
      updatedEditor.commands.setTextSelection({
        from: transaction.selection.from,
        to: transaction.selection.to,
      })

      // Check paraphrase here to save some computationas we know we don't want to fetch any feedback type for paraphrase
      if (
        !isDraftPaper &&
        nonManualUpdateType &&
        !feedbackTypeRequiresNoRecheck.includes(nonManualUpdateType)
      ) {
        newFeedbacksOnEditHandler.fetchNewSentences(
          paperId,
          sentencesEdited,
          initiatedAt.toISOString(),
          nonManualUpdateType,
          dispatch
        )
      }
      // Commit history immediately if it is a non manual update
      debounceOnCommitHistory(Boolean(nonManualUpdateType))
      debounceOnSavePaper(dispatch)
    }
  })
}

export const onEditorUpdate = ({
  editorUpdateEvent: { editor: updatedEditor, transaction },
  sentenceOffsets,
  feedbacks,
  wordCount,
  dispatch,
  paperId,
  paperRequiresRecheck,
  draftQuickActionsFlagEnabled,
  isDraftPaper,
}: OnEditorUpdateParams): void => {
  const updatedEditorText = updatedEditor.getText()
  const isNonManualTextUpdate = transaction.getMeta(IS_NON_MANUAL_TEXT_UPDATE)
  const nonManualUpdateType = transaction.getMeta(NON_MANUAL_UPDATE_TYPE)
  const isHistoryChange = transaction.getMeta(IS_HISTORY_CHANGE)

  const shouldProcessSteps = !isNonManualTextUpdate || nonManualUpdateType
  const { steps: transactionSteps } = transaction

  if (shouldProcessSteps) {
    if (!isHistoryChange && !nonManualUpdateType && transactionSteps.length > 0) {
      if (!paperRequiresRecheck) {
        dispatch(setPaperRequiresRecheck(true))
        history.setEnableReviewButton(true)
      }
    }

    updateWordCountIfChanged(updatedEditorText, wordCount, paperRequiresRecheck, dispatch)

    processTransactionSteps({
      steps: transactionSteps,
      sentenceOffsets,
      feedbacks,
      nonManualUpdateType: nonManualUpdateType as FeedbackType,
      updatedEditor,
      transaction,
      dispatch,
      paperId,
      draftQuickActionsFlagEnabled,
      isDraftPaper,
      isHistoryChange,
    })
  }
  if (isHistoryChange) {
    debounceOnSavePaper(dispatch)
  }
}
